<template>
  <v-dialog :value="show" @input="onClose" max-width="900" scrollable>
    <v-card class="ps-dialog" light>
      <v-card-title>
        {{ isNew ? "Extra anlegen" : draft.name || "Extra bearbeiten" }}
        <v-spacer />
        <v-btn icon @click="onClose"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <!-- ======================= Allgemein ======================= -->
        <div class="ps-dialog-section">
          <div class="ps-dialog-section__head">
            <v-icon small class="mr-2">mdi-gift-outline</v-icon>Allgemein
          </div>
          <v-row dense>
            <v-col cols="12" sm="5">
              <v-text-field v-model="draft.name" label="Name" outlined dense hide-details />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="draft.key"
                label="Shopify-Product-ID (DE)"
                :disabled="!isNew"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-combobox
                v-model="draft.aliasProductIds"
                label="Alias-Product-IDs anderer Shops"
                multiple small-chips deletable-chips
                outlined dense
                append-icon=""
                hint="Product-ID des Extras im anderen Shop (FR, ES, …) eintippen und mit Enter bestätigen — ein Chip pro Shop. Kommas gehen auch."
                persistent-hint
              />
            </v-col>
          </v-row>
        </div>

        <!-- ======================= Preise ======================= -->
        <div class="ps-dialog-section">
          <div class="ps-dialog-section__head">
            <v-icon small class="mr-2">mdi-currency-eur</v-icon>Preise
          </div>

          <div class="ex-subhead">
            Produktionskosten je Variante
            <span class="ex-subhead__hint">Gesamtpreis beim Fulfiller — bei Ersatz-Artikeln (EXT_RE) der komplette Artikel, nicht der Aufpreis</span>
          </div>
          <div class="pv-table-wrap">
            <table class="pv-table">
              <thead>
                <tr>
                  <th class="text-left">Variante (SKU)</th>
                  <th v-for="fk in configuredFulfiller" :key="fk">{{ fulfillerName(fk) }}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-for="(variant, vIndex) in draft.variants" :key="vIndex">
                  <td class="pv-table__input" style="min-width: 220px">
                    <v-text-field
                      v-model="variant.sku"
                      placeholder="EXT_…"
                      dense hide-details solo flat background-color="transparent"
                      class="ex-sku-input"
                    />
                  </td>
                  <td v-for="fk in configuredFulfiller" :key="fk" class="pv-table__input" style="width: 140px">
                    <v-text-field
                      dense hide-details solo flat background-color="transparent"
                      type="number" step="0.01"
                      v-model.number="variant.byFulfiller[fk].costs"
                      placeholder="–"
                    />
                  </td>
                  <td class="text-center" style="width: 40px">
                    <v-btn icon x-small @click="draft.variants.splice(vIndex, 1)">
                      <v-icon x-small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pv-add-row">
            <v-btn small depressed rounded color="primary" @click="addVariant">
              <v-icon small left>mdi-plus</v-icon>Variante
            </v-btn>
          </div>

          <template v-for="fk in configuredFulfiller">
            <div class="ex-subhead mt-5" :key="fk + '-head'">
              Versand je Land — {{ fulfillerName(fk) }}
              <span class="ex-subhead__hint">je Variante; Fallback gilt für Länder ohne Eintrag; leer = Versand der Print-Variante</span>
            </div>
            <div class="pv-table-wrap" :key="fk + '-table'">
              <table class="pv-table">
                <thead>
                  <tr>
                    <th class="text-left">Land</th>
                    <th v-for="(variant, vIndex) in draft.variants" :key="vIndex">{{ shortSku(variant.sku) }}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cc in shippingRowsFor(fk)" :key="cc">
                    <td class="pv-table__label">
                      <template v-if="cc === 'default'">
                        <v-icon x-small class="mr-1">mdi-earth</v-icon>Fallback
                      </template>
                      <template v-else>{{ flag(cc) }} {{ cc }}</template>
                    </td>
                    <td v-for="(variant, vIndex) in draft.variants" :key="vIndex" class="pv-table__input" style="min-width: 90px">
                      <v-text-field
                        dense hide-details solo flat background-color="transparent"
                        type="number" step="0.01"
                        :value="variant.byFulfiller[fk] && variant.byFulfiller[fk].shippingCosts[cc]"
                        placeholder="–"
                        @input="(v) => setVariantShipping(variant, fk, cc, v)"
                      />
                    </td>
                    <td class="text-center" style="width: 40px">
                      <v-btn v-if="cc !== 'default'" icon x-small @click="removeShippingCountryFor(fk, cc)">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="pv-add-row" :key="fk + '-add'">
              <v-select
                dense hide-details outlined
                class="pv-add-row__select"
                :label="`Land hinzufügen (${fulfillerName(fk)})`"
                :items="availableShippingCountriesFor(fk)"
                :value="null"
                @change="(cc) => addShippingCountryFor(fk, cc)"
              />
            </div>
          </template>
        </div>

        <!-- ======================= Fulfillment-Reihenfolge ======================= -->
        <div class="ps-dialog-section">
          <div class="ps-dialog-section__head">
            <v-icon small class="mr-2">mdi-truck-fast-outline</v-icon>Fulfillment-Reihenfolge
          </div>
          <div class="text-caption text--secondary mb-3">
            Sortiert die Fulfiller, die dieses Extra können — leer = Reihenfolge
            der Print-Variante gilt. „Default" greift für alle Länder ohne eigene Regel.
          </div>

          <div
            v-for="cc in rankRows"
            :key="cc"
            class="pv-rank"
            :class="{ 'pv-rank--default': cc === 'default' }"
          >
            <span class="pv-rank__country">
              <template v-if="cc === 'default'">
                <v-icon x-small class="mr-1">mdi-earth</v-icon>Default
              </template>
              <template v-else>{{ flag(cc) }} {{ cc }}</template>
            </span>
            <transition-group name="pv-chip" tag="span" class="pv-rank__chips">
              <span v-for="(fk, index) in draft.fulfillRank[cc]" :key="fk" class="pv-chip">
                <span class="pv-chip__rank">{{ index + 1 }}</span>
                {{ fulfillerName(fk) }}
                <v-icon v-if="index > 0" x-small class="pv-chip__btn" @click="moveRank(cc, index)">mdi-chevron-left</v-icon>
                <v-icon x-small class="pv-chip__btn" @click="draft.fulfillRank[cc].splice(index, 1)">mdi-close</v-icon>
              </span>
            </transition-group>
            <v-menu v-if="rankCandidates(cc).length" offset-y>
              <template v-slot:activator="{ on }">
                <v-btn icon x-small v-on="on" class="pv-rank__add"><v-icon small>mdi-plus</v-icon></v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="fk in rankCandidates(cc)" :key="fk" @click="draft.fulfillRank[cc].push(fk)">
                  <v-list-item-title>{{ fulfillerName(fk) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-spacer />
            <v-btn v-if="cc !== 'default'" icon x-small @click="$delete(draft.fulfillRank, cc)">
              <v-icon x-small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>

          <div class="pv-add-row">
            <v-select
              dense hide-details outlined
              class="pv-add-row__select"
              label="Länder-Regel hinzufügen"
              :items="availableRankCountries"
              :value="null"
              @change="addRankCountry"
            />
          </div>
        </div>

        <!-- ======================= Producer ======================= -->
        <div class="ps-dialog-section">
          <div class="ps-dialog-section__head">
            <v-icon small class="mr-2">mdi-cog-outline</v-icon>Producer-Einstellungen
            <span class="ex-subhead__hint ml-2">Artikel-ID je Variante — ohne ID wird das Extra dort nicht fulfillt</span>
          </div>

          <div v-if="configuredFulfiller.length" class="pv-table-wrap">
            <table class="pv-table">
              <thead>
                <tr>
                  <th class="text-left">Variante (SKU)</th>
                  <th v-for="fk in configuredFulfiller" :key="fk">
                    {{ fulfillerName(fk) }} Artikel-ID
                    <v-icon x-small class="ml-1" style="cursor:pointer" title="Fulfiller entfernen" @click="removeFulfiller(fk)">mdi-delete-outline</v-icon>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(variant, vIndex) in draft.variants" :key="vIndex">
                  <td class="pv-table__label">{{ variant.sku || "SKU?" }}</td>
                  <td v-for="fk in configuredFulfiller" :key="fk" class="pv-table__input" style="min-width: 240px">
                    <v-text-field
                      dense hide-details solo flat background-color="transparent"
                      v-model="variant.byFulfiller[fk].id"
                      placeholder="–"
                    />
                    <div
                      v-if="fk === 'doboxa' && legacyDoboxaId(variant.sku) && !variant.byFulfiller[fk].id"
                      class="ex-suggestion ex-suggestion--table"
                      :title="`Aus bisherigem Code übernehmen: ${legacyDoboxaId(variant.sku)}`"
                      @click="variant.byFulfiller[fk].id = legacyDoboxaId(variant.sku)"
                    >
                      <v-icon x-small class="mr-1">mdi-lightbulb-outline</v-icon>
                      {{ legacyDoboxaId(variant.sku) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="availableFulfiller.length" class="pv-add-row">
            <v-select
              :items="availableFulfiller"
              item-text="name"
              item-value="key"
              dense hide-details outlined
              class="pv-add-row__select"
              label="Fulfiller hinzufügen"
              v-model="selectedFulfiller"
            />
            <v-btn small depressed rounded color="primary" :disabled="!selectedFulfiller" @click="addFulfiller">
              <v-icon small left>mdi-plus</v-icon>Hinzufügen
            </v-btn>
          </div>
        </div>

        <div v-if="error" class="ex-error">{{ error }}</div>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-btn v-if="!isNew" text small color="error" @click="confirmDelete = true">Löschen</v-btn>
        <v-spacer />
        <v-btn text small @click="onClose">Abbrechen</v-btn>
        <v-btn color="success" depressed small :loading="saving" @click="save">Speichern</v-btn>
      </v-card-actions>

      <ConfirmDialog
        :show="confirmDelete"
        :on-cancel="() => (confirmDelete = false)"
        :on-accept="doDelete"
        icon="mdi-trash-can-outline"
        title="Extra löschen"
        :message="`„${draft.name || draft.key}“ wird aus den Extras entfernt. Bestellungen mit diesem Extra können danach ggf. nicht mehr automatisch zugeordnet werden. Fortfahren?`"
        accept-text="LÖSCHEN"
        accept-color="error"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import ConfirmDialog from "@/components/ConfirmDialog";
import { countryCodes } from "@/constants";

const _ = require("lodash");

/** Bisher hartcodierte Doboxa-IDs (doboxa/createOrder.js) als Vorbefüllung */
const LEGACY_DOBOXA_MAPPING = {
  EXT_FRAMEA_A4: "PF-FRAME-AB21-30",
  EXT_FRAMEA_A3: "PF-FRAME-AB30-42",
  EXT_FRAMEA_A2: "PF-FRAME-AB42-60",
  EXT_FRAMEW_A4: "PF-FRAME-WNN21-30",
  EXT_FRAMEW_A3: "PF-FRAME-WNN30-42",
  EXT_FRAMEW_A2: "PF-FRAME-WNN42-60",
};

/** Gleiche Fulfiller-Liste wie im Print-Varianten-Dialog */
const ALL_FULFILLER = [
  { key: "doboxa", name: "Doboxa" },
  { key: "myPoster", name: "MyPoster" },
  { key: "picanova", name: "Picanova" },
  { key: "picanovaUS", name: "merchOne US" },
  { key: "manual", name: "Manuell" },
];

export default {
  name: "ExtraDialog",
  components: { ConfirmDialog },
  props: {
    show: { type: Boolean, required: true },
    onClose: { type: Function, required: true },
    extra: { type: Object, default: null },
  },
  data() {
    return {
      draft: this.buildDraft(this.extra),
      selectedFulfiller: "",
      saving: false,
      confirmDelete: false,
      error: "",
    };
  },
  computed: {
    isNew() {
      return !this.extra;
    },
    configuredFulfiller() {
      return Object.keys(this.draft.fulfiller);
    },
    availableFulfiller() {
      return ALL_FULFILLER.filter((f) => !this.draft.fulfiller[f.key]);
    },
    rankRows() {
      const keys = Object.keys(this.draft.fulfillRank).filter((cc) => cc !== "default");
      return ["default", ...keys.sort()];
    },
    availableRankCountries() {
      const used = new Set(Object.keys(this.draft.fulfillRank));
      return this.countryItems.filter((item) => !used.has(item.value));
    },
    countryItems() {
      return Object.keys(countryCodes).map((cc) => ({
        value: cc,
        text: `${this.flag(cc)} ${cc}`,
      }));
    },
  },
  methods: {
    ...mapActions(["saveExtraFull", "deleteExtra"]),
    flag(cc) {
      if (!/^[A-Z]{2}$/.test(cc)) return "";
      return String.fromCodePoint(
        ...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
      );
    },
    fulfillerName(key) {
      return ALL_FULFILLER.find((f) => f.key === key)?.name || key;
    },
    /** Firestore-Form -> Entwurf.
     *  extras/{pid}: { fulfiller: {fk: {shippingCosts, variants: {sku: {costs,id}}}},
     *                  fulfillRank, variants: {sku: Legacy-Länderpreise} } */
    buildDraft(extra) {
      const fulfiller = {};
      for (const fk of Object.keys(extra?.fulfiller || {})) {
        fulfiller[fk] = {};
      }
      // SKU-Liste: Legacy-Varianten + alle bei Fulfillern gepflegten
      const skus = new Set(Object.keys(extra?.variants || {}));
      for (const config of Object.values(extra?.fulfiller || {})) {
        for (const sku of Object.keys(config.variants || {})) skus.add(sku);
      }
      const variants = [...skus].sort().map((sku) => {
        const byFulfiller = {};
        for (const fk of Object.keys(fulfiller)) {
          const config = extra?.fulfiller?.[fk]?.variants?.[sku];
          byFulfiller[fk] = {
            costs: typeof config?.costs === "number" ? config.costs : null,
            id: config?.id || "",
            // Versand je Variante; Übergang: früher auf Fulfiller-Ebene gepflegte
            // Werte werden als Startwert übernommen
            shippingCosts: {
              ...(extra?.fulfiller?.[fk]?.shippingCosts || {}),
              ...(config?.shippingCosts || {}),
            },
          };
        }
        return { sku, byFulfiller, legacy: { ...(extra?.variants?.[sku] || {}) } };
      });
      const fulfillRank = {};
      for (const [cc, list] of Object.entries(extra?.fulfillRank || {})) {
        fulfillRank[cc] = [...(list || [])];
      }
      if (!fulfillRank.default) fulfillRank.default = [];
      return {
        key: extra?.key || "",
        name: extra?.name || "",
        aliasProductIds: [...(extra?.aliasProductIds || [])],
        fulfiller,
        fulfillRank,
        variants: variants.length
          ? variants
          : [{ sku: "", byFulfiller: {}, legacy: {} }],
      };
    },
    /** Entwurf -> Firestore-Form. Legacy-Länderpreise werden unverändert
     *  durchgereicht (Statistik-Fallback für Alt-Daten). */
    serialize() {
      const variants = {};
      const fulfiller = {};
      for (const fk of this.configuredFulfiller) {
        fulfiller[fk] = { variants: {} };
      }
      for (const variant of this.draft.variants) {
        const sku = (variant.sku || "").trim();
        if (!sku) continue;
        variants[sku] = { ...variant.legacy };
        for (const fk of this.configuredFulfiller) {
          const config = variant.byFulfiller[fk] || {};
          fulfiller[fk].variants[sku] = {
            id: (config.id || "").trim(),
            ...(config.costs != null && config.costs !== ""
              ? { costs: Number(config.costs) }
              : {}),
            ...(Object.keys(config.shippingCosts || {}).length
              ? { shippingCosts: _.mapValues(config.shippingCosts, Number) }
              : {}),
          };
        }
      }
      const fulfillRank = {};
      for (const [cc, list] of Object.entries(this.draft.fulfillRank)) {
        const cleaned = (list || []).filter((fk) => fulfiller[fk]);
        if (cleaned.length) fulfillRank[cc] = cleaned;
      }
      return {
        name: (this.draft.name || "").trim(),
        // Komma-/Leerzeichen-getrennte Eingaben in einzelne IDs zerlegen —
        // gemeint ist immer eine Liste von Product-IDs
        aliasProductIds: (this.draft.aliasProductIds || [])
          .flatMap((id) => id.toString().split(/[\s,;]+/))
          .map((id) => id.trim())
          .filter(Boolean),
        variants,
        ...(Object.keys(fulfiller).length ? { fulfiller } : {}),
        ...(Object.keys(fulfillRank).length ? { fulfillRank } : {}),
      };
    },
    // --- Fulfiller ---
    addFulfiller() {
      const fk = this.selectedFulfiller;
      if (!fk) return;
      this.$set(this.draft.fulfiller, fk, {});
      for (const variant of this.draft.variants) {
        this.$set(variant.byFulfiller, fk, { costs: null, id: "", shippingCosts: {} });
      }
      this.selectedFulfiller = "";
    },
    removeFulfiller(fk) {
      this.$delete(this.draft.fulfiller, fk);
      for (const variant of this.draft.variants) {
        this.$delete(variant.byFulfiller, fk);
      }
      for (const list of Object.values(this.draft.fulfillRank)) {
        const index = list.indexOf(fk);
        if (index >= 0) list.splice(index, 1);
      }
    },
    // --- Varianten ---
    addVariant() {
      const byFulfiller = {};
      for (const fk of this.configuredFulfiller) {
        byFulfiller[fk] = { costs: null, id: "", shippingCosts: {} };
      }
      this.draft.variants.push({ sku: "", byFulfiller, legacy: {} });
    },
    /** Kompakter Spaltenkopf: EXT_/EXT_RE_-Präfix weglassen */
    shortSku(sku) {
      return (sku || "").replace(/^EXT_(RE_)?/, "") || "SKU?";
    },
    // --- Versand (je Land UND Variante) ---
    shippingRowsFor(fk) {
      const set = new Set();
      for (const variant of this.draft.variants) {
        for (const cc of Object.keys(variant.byFulfiller[fk]?.shippingCosts || {})) set.add(cc);
      }
      set.delete("default");
      return ["default", ...[...set].sort()];
    },
    availableShippingCountriesFor(fk) {
      const used = new Set(this.shippingRowsFor(fk));
      return this.countryItems.filter((item) => !used.has(item.value));
    },
    setVariantShipping(variant, fk, cc, value) {
      if (!variant.byFulfiller[fk]) return;
      const table = variant.byFulfiller[fk].shippingCosts;
      if (value === "" || value === null) this.$delete(table, cc);
      else this.$set(table, cc, Number(value));
    },
    addShippingCountryFor(fk, cc) {
      if (!cc) return;
      const first = this.draft.variants[0];
      if (first && first.byFulfiller[fk]) this.$set(first.byFulfiller[fk].shippingCosts, cc, 0);
    },
    removeShippingCountryFor(fk, cc) {
      for (const variant of this.draft.variants) {
        this.$delete(variant.byFulfiller[fk]?.shippingCosts || {}, cc);
      }
    },
    // --- Reihenfolge ---
    rankCandidates(cc) {
      const list = this.draft.fulfillRank[cc] || [];
      return this.configuredFulfiller.filter((fk) => !list.includes(fk));
    },
    moveRank(cc, index) {
      if (index === 0) return;
      const list = this.draft.fulfillRank[cc];
      const [item] = list.splice(index, 1);
      list.splice(index - 1, 0, item);
    },
    addRankCountry(cc) {
      if (!cc) return;
      this.$set(this.draft.fulfillRank, cc, [...this.draft.fulfillRank.default]);
    },
    // --- Producer ---
    legacyDoboxaId(sku) {
      const trimmed = (sku || "").trim();
      if (LEGACY_DOBOXA_MAPPING[trimmed]) return LEGACY_DOBOXA_MAPPING[trimmed];
      // EXT_RE_<DoboxaId>: ID steckt im SKU selbst
      const parts = trimmed.split("_");
      if (parts[1] === "RE" && parts[2]) return parts.slice(2).join("_");
      return null;
    },
    // --- Speichern ---
    async save() {
      this.error = "";
      const key = (this.draft.key || "").trim();
      if (!key) {
        this.error = "Shopify-Product-ID fehlt.";
        return;
      }
      const data = this.serialize();
      if (!Object.keys(data.variants).length) {
        this.error = "Mindestens eine Variante mit SKU anlegen.";
        return;
      }
      this.saving = true;
      try {
        await this.saveExtraFull({ key, extra: data });
        this.onClose();
      } catch (e) {
        this.error = e.message || "Speichern fehlgeschlagen";
      } finally {
        this.saving = false;
      }
    },
    async doDelete() {
      this.confirmDelete = false;
      this.saving = true;
      try {
        await this.deleteExtra(this.draft.key);
        this.onClose();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ex-sku-input {
  ::v-deep input {
    font-weight: 600;
    font-size: 13px;
  }
}

.ex-subhead {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 8px;

  &__hint {
    font-size: 11px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    color: rgba(0, 0, 0, 0.4);
  }
}

.ex-suggestion {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  color: #a96617;
  background: #fdf4e3;
  border-radius: 8px;
  padding: 4px 10px;
  margin-top: 4px;
  cursor: pointer;

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    background: #fdeeda;
  }

  &--table {
    margin: 0 0 4px;
    padding: 2px 8px;
    font-size: 11px;
  }
}

.ex-error {
  color: #b71c1c;
  font-size: 13px;
  margin-top: 4px;
}

/* Identisch zum Print-Varianten-Dialog (dort scoped, daher hier dupliziert) */
.pv-add-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;

  &__select {
    max-width: 220px;
  }
}

.pv-rank {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 8px;

  &--default {
    background: rgba(63, 157, 104, 0.06);
    border-color: rgba(63, 157, 104, 0.25);

    .pv-rank__country {
      color: #2f7d52;
    }
  }

  &__country {
    font-size: 13px;
    font-weight: 700;
    min-width: 74px;
    display: inline-flex;
    align-items: center;
  }

  &__chips {
    display: inline-flex;
    gap: 6px;
    flex-wrap: wrap;
  }
}

.pv-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eef0f3;
  border-radius: 999px;
  padding: 3px 10px 3px 4px;
  font-size: 12.5px;
  font-weight: 600;

  &__rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background: #fff;
    font-size: 11px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  }

  &__btn {
    cursor: pointer;
    opacity: 0.55;

    &:hover {
      opacity: 1;
    }
  }
}

.pv-chip-move,
.pv-chip-enter-active,
.pv-chip-leave-active {
  transition: all 0.2s ease;
}
.pv-chip-enter,
.pv-chip-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
